import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, X, Plus, Minus, Loader2, Palette, Package, SprayCan, Droplet, Eraser, CheckCircle2, Clock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useCart } from "../lib/cart";
import MetamarkComparison from "../components/MetamarkComparison";
import MetamarkColorPalette from "../components/MetamarkColorPalette";

export default function StickersPage() {
    const [stickers, setStickers] = useState([]);
    const [cat, setCat] = useState("all");
    const [loading, setLoading] = useState(true);
    const [buying, setBuying] = useState(null);
    const { user } = useAuth();

    useEffect(() => {
        api.get("/stickers")
            .then(({ data }) => setStickers(data || []))
            .catch(() => setStickers([]))
            .finally(() => setLoading(false));
    }, []);

    const cats = ["all", ...Array.from(new Set(stickers.map(s => s.category))).sort()];
    // Op deze pagina tonen we ENKEL stickers die te koop zijn (admin schakelt for_sale aan)
    const visible = stickers.filter(s => s.for_sale);
    const filtered = cat === "all" ? visible : visible.filter(s => s.category === cat);

    return (
        <div data-testid="stickers-page" className="bg-zinc-50 min-h-screen">
            {/* HERO */}
            <section className="border-b border-zinc-200 bg-white">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
                    <p className="label-tiny text-rose-600">★ Stickers · Accessoires · Cadeaus</p>
                    <h1 className="font-display text-5xl md:text-6xl mt-2 tracking-tight">
                        Stickers &amp; <span className="text-rose-600">cadeaus</span>.
                    </h1>
                    <p data-testid="stickers-slogan" className="font-display text-2xl md:text-3xl mt-3 italic text-zinc-500">
                        Maak het persoonlijk. <span className="text-rose-600 not-italic font-bold">Geef het door.</span>
                    </p>
                    <p className="text-zinc-600 mt-4 max-w-2xl text-sm">
                        Hoogwaardige vinylstickers voor auto, ruit &amp; helm — plus accessoires, pakketten en cadeau-aanbod.
                        Eén shop voor alles wat plakt of pakt.
                    </p>
                    <div className="mt-3 inline-flex items-start gap-2 px-3 py-2 rounded-xl" style={{ background: "var(--rose-tint)" }} data-testid="mono-notice">
                        <Palette size={14} style={{ color: "var(--rose-deep)" }} className="mt-0.5 shrink-0" />
                        <p className="text-xs leading-relaxed" style={{ color: "var(--text)" }}>
                            <strong>Let op:</strong> alle stickers en logo&apos;s worden gesneden in <strong>één enkele kleur</strong> (mono) uit
                            Metamark vinyl — geen full-colour print. Kies je gewenste vinylkleur per sticker.
                        </p>
                    </div>
                    <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
                        <span data-testid="stock-info-instock" className="inline-flex items-center gap-1.5 text-emerald-700">
                            <CheckCircle2 size={14} className="text-emerald-600" />
                            <span><strong>Sommige stickers</strong> hebben we direct op voorraad — snel verzonden.</span>
                        </span>
                        <span data-testid="stock-info-madetoorder" className="inline-flex items-center gap-1.5 text-amber-700">
                            <Clock size={14} className="text-amber-600" />
                            <span><strong>Andere maken we op bestelling</strong> — productietijd 3-7 werkdagen.</span>
                        </span>
                    </div>
                    <div className="mt-8 flex flex-wrap gap-3">
                        <Link
                            to="/tegel-designer"
                            data-testid="stickers-tegel-cta"
                            className="btn-primary inline-flex items-center gap-2"
                        >
                            <Sparkles size={14} /> Ontwerp je eigen tegeltje →
                        </Link>
                        <p className="text-xs text-zinc-500 self-center max-w-xs">
                            Op maat keramisch tegeltje 15×15cm — eigen tekst, perfect als cadeau.
                        </p>
                    </div>
                </div>
            </section>

            {/* INFO STRIP */}
            <section className="border-b border-zinc-200 bg-zinc-50">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6 grid grid-cols-1 md:grid-cols-4 gap-6 text-sm">
                    <div>
                        <p className="label-tiny text-rose-600 mb-1">★ Weerbestendig</p>
                        <p className="font-semibold">UV- &amp; waterbestendig</p>
                        <p className="text-xs text-zinc-500 mt-0.5">Premium vinyl met UV-beschermende coating. Verkleurt niet en houdt 5+ jaar buiten.</p>
                    </div>
                    <div>
                        <p className="label-tiny text-rose-600 mb-1">★ Glad oppervlak</p>
                        <p className="font-semibold">Auto, glas, helm, koffer</p>
                        <p className="text-xs text-zinc-500 mt-0.5">Plakt perfect op carrosserie, autoruiten, motorhelmen, laptops en alle gladde, niet-poreuze oppervlakken.</p>
                    </div>
                    <div>
                        <p className="label-tiny text-rose-600 mb-1">★ Resteloos verwijderbaar</p>
                        <p className="font-semibold">Geen lijmresten</p>
                        <p className="text-xs text-zinc-500 mt-0.5">Verwijder zonder schade aan de ondergrond — ideaal voor leasewagens of tijdelijke reclame.</p>
                    </div>
                    <div>
                        <p className="label-tiny text-rose-600 mb-1">★ Plak je zelf</p>
                        <p className="font-semibold">Met transfertape geleverd</p>
                        <p className="text-xs text-zinc-500 mt-0.5">Stappenplan inbegrepen. Werkt het beste op een schoon, vetvrij oppervlak bij min. 10°C.</p>
                    </div>
                </div>
            </section>

            {/* FILTERS */}
            <section className="border-b border-zinc-200 bg-white sticky top-0 z-30">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-3 flex flex-wrap items-center gap-2">
                    {cats.map(c => {
                        const count = c === "all" ? stickers.length : stickers.filter(s => s.category === c).length;
                        if (count === 0 && c !== "all") return null;
                        return (
                            <button
                                key={c}
                                data-testid={`stickers-tab-${c}`}
                                onClick={() => setCat(c)}
                                className={`text-[10px] px-2.5 py-1.5 border uppercase tracking-widest transition-colors ${cat === c ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-200 hover:border-zinc-950"}`}>
                                {c === "all" ? "Alles" : c} ({count})
                            </button>
                        );
                    })}
                    <p className="ml-auto text-xs text-zinc-500">{filtered.length} stickers</p>
                </div>
            </section>

            {/* GRID */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
                {loading ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                        {Array.from({ length: 10 }).map((_, i) => (
                            <div key={i} className="aspect-square bg-zinc-100 border border-zinc-200 animate-pulse" />
                        ))}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="border border-dashed border-zinc-300 bg-white p-12 text-center">
                        <p className="font-display text-2xl">Geen stickers</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-0 border-l border-t border-zinc-200">
                        {filtered.map(s => (
                            <StickerCard key={s.id} sticker={s} onBuy={() => setBuying(s)} />
                        ))}
                    </div>
                )}

                <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-0 border-l border-t border-zinc-200">
                    <div className="border-r border-b border-zinc-200 bg-white p-6">
                        <p className="label-tiny text-rose-600 mb-2">★ Hoe plak je je sticker?</p>
                        <ol className="text-sm text-zinc-700 space-y-1.5 list-decimal pl-4">
                            <li>Maak het oppervlak grondig schoon met glasreiniger of isopropanol — zonder vet of stof.</li>
                            <li>Wacht tot het oppervlak volledig droog is. Werk bij minimum <strong>10°C</strong>.</li>
                            <li>Positioneer de sticker met de transfertape nog erop. Plak eerst een hoek en strijk dan glad van binnen naar buiten.</li>
                            <li>Gebruik een rakel of bankkaart om luchtbellen weg te wrijven.</li>
                            <li>Trek de transfertape <strong>langzaam onder een hoek van 180°</strong> af. Klaar!</li>
                        </ol>
                    </div>
                    <div className="border-r border-b border-zinc-200 bg-white p-6">
                        <p className="label-tiny text-rose-600 mb-2">★ Onderhoud &amp; tips</p>
                        <ul className="text-sm text-zinc-700 space-y-1.5 list-disc pl-4">
                            <li>Veilig in carwash bij normale druk — geen hogedrukreiniger direct op de rand.</li>
                            <li>UV-bestendig &gt; 5 jaar bij gemiddeld klimaat.</li>
                            <li>Niet aanbevolen op poreuze oppervlakken (hout, baksteen, ruwe muren).</li>
                            <li>Voor autoruiten: kies bij voorkeur de <strong>binnenkant</strong> voor extra bescherming.</li>
                            <li>Bestel jouw favoriete kleur — alle stickers zijn herkleurd op aanvraag.</li>
                        </ul>
                    </div>
                </div>

                <div className="mt-10 border border-zinc-200 bg-zinc-950 text-white p-6 flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <p className="label-tiny text-rose-400 mb-1">★ Bulk &amp; op maat</p>
                        <p className="font-display text-2xl">Sticker op maat nodig?</p>
                        <p className="text-zinc-400 text-sm mt-1 max-w-xl">
                            Eigen logo, bedrijfslogo, voertuig-belettering of grote oplages? We maken het op maat — vraag een offerte aan.
                        </p>
                    </div>
                    <Link to="/bulk-order" data-testid="stickers-bulk-cta" className="btn-primary bg-white text-zinc-950 hover:bg-zinc-100">
                        Vraag offerte aan →
                    </Link>
                </div>
            </section>

            {/* ACCESSOIRES → verplaatst naar /accessoires */}
            {/* BUNDLES → verwijderd */}

            {/* METAMARK M4 vs M7 — productvergelijking */}
            <section id="vergelijking" data-testid="comparison-section" className="border-t border-[var(--border-soft)]" style={{ background: "var(--bg)" }}>
                <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16 space-y-10">
                    <MetamarkComparison />
                    <div id="kleuren">
                        <MetamarkColorPalette />
                    </div>
                </div>
            </section>

            {/* SHOP / GIFTS — verwijderd uit publieke pagina */}

            {buying && (
                <BuyStickerModal
                    sticker={buying}
                    user={user}
                    onClose={() => setBuying(null)}
                />
            )}
        </div>
    );
}

function StickerCard({ sticker, onBuy }) {
    const colors = sticker.available_colors || [];
    const [previewColor, setPreviewColor] = useState(colors[0] || "#000000");

    return (
        <article
            data-testid={`sticker-tile-${sticker.id}`}
            className="group border-r border-b border-zinc-200 bg-white p-4 flex flex-col">
            <div className="aspect-square bg-zinc-50 border border-zinc-100 p-0 flex items-center justify-center overflow-hidden">
                {sticker.image_url ? (
                    <img
                        src={sticker.image_url}
                        alt={sticker.name}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = "none"; }}
                    />
                ) : (
                    <div
                        className="w-full h-full transition-colors duration-200 p-6"
                        style={{ color: previewColor }}
                        dangerouslySetInnerHTML={{ __html: sticker.svg }}
                    />
                )}
            </div>
            <div className="mt-3 flex-1 flex flex-col">
                <div className="flex items-baseline justify-between gap-2">
                    <p className="font-semibold text-sm truncate">{sticker.name}</p>
                    {sticker.for_sale && <p className="font-mono-dm text-xs whitespace-nowrap">€{(sticker.price || 0).toFixed(2)}</p>}
                </div>
                <p className="label-tiny mt-0.5 text-zinc-400">{sticker.category}</p>

                {/* Color chips */}
                {colors.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1" data-testid={`sticker-colors-${sticker.id}`}>
                        {colors.slice(0, 10).map((hex) => (
                            <button
                                key={hex}
                                data-testid={`sticker-color-chip-${sticker.id}-${hex}`}
                                onClick={() => setPreviewColor(hex)}
                                title={hex}
                                className={`w-4 h-4 border transition-all ${previewColor === hex ? "border-zinc-950 ring-1 ring-zinc-950 scale-110" : "border-zinc-300 hover:border-zinc-500"}`}
                                style={{ background: hex }}
                            />
                        ))}
                        {colors.length > 10 && (
                            <span className="text-[10px] text-zinc-500 self-center ml-0.5">+{colors.length - 10}</span>
                        )}
                    </div>
                )}

                {/* Action */}
                <div className="mt-auto pt-3 border-t border-zinc-100 mt-3 flex gap-2 items-center">
                    {sticker.for_sale ? (
                        <button
                            data-testid={`sticker-buy-${sticker.id}`}
                            onClick={() => onBuy()}
                            className="flex-1 text-[10px] py-2 bg-zinc-950 text-white hover:bg-zinc-800 font-bold uppercase tracking-widest inline-flex items-center justify-center gap-1.5">
                            <ShoppingBag size={11} /> Koop
                        </button>
                    ) : (
                        <Link
                            to="/designer"
                            data-testid={`sticker-use-${sticker.id}`}
                            className="flex-1 text-[10px] py-2 border border-zinc-300 hover:border-zinc-950 text-center font-bold uppercase tracking-widest">
                            Gebruik in design
                        </Link>
                    )}
                </div>
            </div>
        </article>
    );
}

function BuyStickerModal({ sticker, user, onClose }) {
    const colors = sticker.available_colors || ["#000000"];
    const sizes = sticker.sale_sizes || [];
    const [color, setColor] = useState(colors[0] || "#000000");
    const [size, setSize] = useState(sizes[0] || null);
    const [qty, setQty] = useState(1);
    const [includeTape, setIncludeTape] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Tape-fee berekening (frontend preview): € 0,10 per cm van de grootste afmeting, minimum € 1,00
    const tapeFee = (() => {
        if (!size) return 0;
        const maxMm = Math.max(parseInt(size.width_mm) || 0, parseInt(size.height_mm) || 0);
        return Math.max((maxMm / 10) * 0.10, 1.00);
    })();
    const unitPrice = (parseFloat(size?.price) || 0) + (includeTape ? tapeFee : 0);

    const addToCart = async () => {
        if (!user) { toast.error("Log eerst in"); return; }
        if (!size) { toast.error("Kies een formaat"); return; }
        setSubmitting(true);
        try {
            await api.post("/cart/items/sticker", {
                sticker_id: sticker.id,
                color,
                size_label: size.label,
                quantity: qty,
                include_tape: includeTape,
            });
            toast.success(`${sticker.name} toegevoegd aan winkelmandje${includeTape ? " (incl. applicatietape)" : ""}`);
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Kon niet toevoegen");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div onClick={onClose} className="fixed inset-0 z-[80] bg-black/75 backdrop-blur-sm flex items-center justify-center p-6 fade-in">
            <div onClick={(e) => e.stopPropagation()} data-testid="sticker-buy-modal" className="bg-white max-w-md w-full max-h-[92vh] overflow-y-auto">
                <div className="p-5 border-b border-zinc-100 flex items-center justify-between">
                    <div>
                        <p className="label-tiny">{sticker.category}</p>
                        <p className="font-display text-2xl mt-1">{sticker.name}</p>
                    </div>
                    <button onClick={onClose} className="text-zinc-400 hover:text-zinc-950"><X size={18} /></button>
                </div>
                <div className="aspect-square bg-zinc-50 p-10 flex items-center justify-center">
                    <div className="w-full h-full transition-colors" style={{ color }} dangerouslySetInnerHTML={{ __html: sticker.svg }} />
                </div>
                <div className="p-5 space-y-4 border-t border-zinc-100">
                    {/* Color picker */}
                    <div>
                        <p className="label-tiny mb-2 flex items-center gap-1.5"><Palette size={12} /> Kleur</p>
                        <div className="flex flex-wrap gap-1.5">
                            {colors.map(hex => (
                                <button
                                    key={hex}
                                    data-testid={`buy-color-${hex}`}
                                    onClick={() => setColor(hex)}
                                    title={hex}
                                    className={`w-8 h-8 border-2 transition-transform hover:scale-110 ${color === hex ? "border-zinc-950 ring-2 ring-zinc-950 ring-offset-1" : "border-zinc-200"}`}
                                    style={{ background: hex }}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Size */}
                    {sizes.length > 0 ? (
                        <div>
                            <p className="label-tiny mb-2">Formaat</p>
                            <div className="space-y-2">
                                {sizes.map(s => (
                                    <button
                                        key={s.label}
                                        data-testid={`buy-size-${s.label}`}
                                        onClick={() => setSize(s)}
                                        className={`w-full text-left px-3 py-2.5 border flex items-center justify-between text-sm ${size?.label === s.label ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 hover:border-zinc-950"}`}>
                                        <div>
                                            <p className="font-bold">{s.label}</p>
                                            <p className="text-[10px] opacity-70">{s.width_mm}×{s.height_mm}mm</p>
                                        </div>
                                        <span className="font-mono-dm">€ {parseFloat(s.price).toFixed(2)}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-3">⚠ Geen formaten ingesteld. Admin moet formaten/prijzen toevoegen.</p>
                    )}

                    {/* Qty */}
                    <div>
                        <p className="label-tiny mb-2">Aantal</p>
                        <div className="inline-flex items-stretch border border-zinc-300">
                            <button data-testid="buy-qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 hover:bg-zinc-50"><Minus size={14} /></button>
                            <span className="px-5 py-2 font-bold text-sm min-w-[50px] text-center">{qty}</span>
                            <button data-testid="buy-qty-plus" onClick={() => setQty(Math.min(99, qty + 1))} className="px-3 hover:bg-zinc-50"><Plus size={14} /></button>
                        </div>
                    </div>

                    {/* Applicatietape op maat — betalend */}
                    {size && (
                        <label
                            data-testid="buy-tape-toggle"
                            className={`flex items-start gap-3 p-3 border cursor-pointer transition-colors ${includeTape ? "border-zinc-950 bg-zinc-50" : "border-zinc-300 hover:border-zinc-500"}`}>
                            <input
                                type="checkbox"
                                checked={includeTape}
                                onChange={(e) => setIncludeTape(e.target.checked)}
                                className="mt-0.5 w-4 h-4 accent-zinc-950"
                                data-testid="buy-tape-checkbox"
                            />
                            <div className="flex-1">
                                <div className="flex items-center justify-between gap-2">
                                    <p className="font-bold text-sm">+ Applicatietape op maat</p>
                                    <p className="font-mono-dm text-sm" data-testid="buy-tape-price">+ € {tapeFee.toFixed(2)}</p>
                                </div>
                                <p className="text-[11px] text-zinc-500 mt-1 leading-snug">
                                    Wordt op het exacte formaat van je sticker meegeleverd. Aanbevolen voor een vlekkeloos resultaat — vooral voor stickers groter dan 10 cm.
                                </p>
                            </div>
                        </label>
                    )}

                    {/* Total + submit */}
                    <div className="flex items-center justify-between border-t border-zinc-100 pt-4">
                        <div>
                            <p className="label-tiny">Totaal</p>
                            <p className="font-display text-2xl mt-1" data-testid="buy-total">€ {(unitPrice * qty).toFixed(2)}</p>
                            {includeTape && (
                                <p className="text-[10px] text-zinc-500 mt-0.5">incl. applicatietape € {tapeFee.toFixed(2)}/stuk</p>
                            )}
                        </div>
                        {!user ? (
                            <Link to={`/login?next=${encodeURIComponent("/stickers")}`} className="btn-primary">Log in</Link>
                        ) : (
                            <button
                                onClick={addToCart}
                                disabled={submitting || !size}
                                data-testid="buy-confirm"
                                className="btn-primary inline-flex items-center gap-2 disabled:opacity-60">
                                {submitting ? <Loader2 size={14} className="animate-spin" /> : <ShoppingBag size={14} />}
                                {submitting ? "Toevoegen…" : "Toevoegen"}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function AccessoryCard({ accessory, user }) {
    const { fetchCart } = useCart();
    const [qty, setQty] = useState(1);
    const [adding, setAdding] = useState(false);

    const Icon =
        accessory.icon === "tape" ? Package
        : accessory.icon === "spray" ? SprayCan
        : accessory.icon === "cleaner" ? Droplet
        : accessory.icon === "remover" ? Eraser
        : accessory.icon === "color-card" ? Palette
        : Package;

    const add = async () => {
        if (!user) { toast.error("Log eerst in om toe te voegen aan je mandje"); return; }
        setAdding(true);
        try {
            await api.post("/cart/items/accessory", { accessory_id: accessory.id, quantity: qty });
            await fetchCart();
            toast.success(`${accessory.name} toegevoegd aan winkelmandje`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Kon niet toevoegen");
        } finally {
            setAdding(false);
        }
    };

    return (
        <article
            data-testid={`accessory-card-${accessory.slug}`}
            className="group border-r border-b border-zinc-200 bg-white p-6 flex flex-col gap-4 hover:bg-zinc-50/50 transition-colors">
            <div className="flex items-start justify-between gap-3">
                {accessory.image_url ? (
                    <div className="w-20 h-20 border border-zinc-200 bg-white overflow-hidden shrink-0">
                        <img
                            src={accessory.image_url}
                            alt={accessory.name}
                            className="w-full h-full object-contain"
                            onError={(e) => { e.target.style.display = "none"; }}
                            data-testid={`accessory-image-${accessory.slug}`}
                        />
                    </div>
                ) : (
                    <div className="w-12 h-12 border border-zinc-200 bg-zinc-50 flex items-center justify-center shrink-0">
                        <Icon size={22} strokeWidth={1.5} className="text-zinc-700" />
                    </div>
                )}
                <div className="text-right">
                    <p className="font-mono-dm text-lg">€ {(accessory.price || 0).toFixed(2)}</p>
                    {(() => {
                        const tracked = accessory.stock_count !== null && accessory.stock_count !== undefined;
                        if (!accessory.in_stock) {
                            return (
                                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-amber-700 mt-1">
                                    <Clock size={10} /> Op bestelling
                                </span>
                            );
                        }
                        if (!tracked) {
                            return (
                                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-700 mt-1">
                                    <CheckCircle2 size={10} /> Op voorraad
                                </span>
                            );
                        }
                        if (accessory.stock_count === 0) {
                            return (
                                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-rose-700 mt-1">
                                    Uitverkocht
                                </span>
                            );
                        }
                        if (accessory.stock_count <= 3) {
                            return (
                                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-amber-700 mt-1">
                                    Nog {accessory.stock_count} {accessory.stock_count === 1 ? "stuk" : "stuks"}
                                </span>
                            );
                        }
                        return (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-700 mt-1">
                                <CheckCircle2 size={10} /> {accessory.stock_count} op voorraad
                            </span>
                        );
                    })()}
                </div>
            </div>
            <div className="flex-1">
                <p className="font-display text-xl leading-tight">{accessory.name}</p>
                <p className="label-tiny mt-1 text-zinc-400">{accessory.unit}</p>
                <p className="text-sm text-zinc-600 mt-3">{accessory.short}</p>
            </div>
            <div className="flex items-center gap-2 border-t border-zinc-100 pt-4">
                <div className="inline-flex items-stretch border border-zinc-300">
                    <button
                        data-testid={`acc-qty-minus-${accessory.slug}`}
                        onClick={() => setQty(Math.max(1, qty - 1))}
                        className="px-2.5 hover:bg-zinc-50"><Minus size={12} /></button>
                    <span className="px-3.5 py-2 font-bold text-xs min-w-[36px] text-center">{qty}</span>
                    <button
                        data-testid={`acc-qty-plus-${accessory.slug}`}
                        onClick={() => setQty(Math.min(50, qty + 1))}
                        className="px-2.5 hover:bg-zinc-50"><Plus size={12} /></button>
                </div>
                {!user ? (
                    <Link
                        to={`/login?next=${encodeURIComponent("/stickers")}`}
                        data-testid={`acc-login-${accessory.slug}`}
                        className="flex-1 text-[10px] py-2.5 border border-zinc-950 text-center font-bold uppercase tracking-widest hover:bg-zinc-950 hover:text-white transition-colors">
                        Log in om te kopen
                    </Link>
                ) : (
                    <button
                        data-testid={`acc-add-${accessory.slug}`}
                        onClick={add}
                        disabled={adding}
                        className="flex-1 text-[10px] py-2.5 bg-zinc-950 text-white hover:bg-zinc-800 font-bold uppercase tracking-widest inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
                        {adding ? <Loader2 size={12} className="animate-spin" /> : <ShoppingBag size={12} />}
                        {adding ? "…" : "In mandje"}
                    </button>
                )}
            </div>
        </article>
    );
}


function BundleCard({ bundle, user }) {
    const { fetchCart } = useCart();
    const [adding, setAdding] = useState(false);
    const savings = bundle.base_price - bundle.final_price;
    const add = async () => {
        if (!user) { toast.error("Log eerst in"); return; }
        setAdding(true);
        try {
            await api.post("/cart/items/bundle", { bundle_id: bundle.id, quantity: 1 });
            await fetchCart();
            toast.success(`${bundle.name} toegevoegd aan mandje`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Toevoegen mislukt");
        } finally { setAdding(false); }
    };
    return (
        <article data-testid={`bundle-card-${bundle.slug}`} className="border border-rose-200 bg-white p-5 flex flex-col gap-3 hover:border-rose-400 transition-colors">
            {bundle.image_url && (
                <div className="aspect-video border border-zinc-100 overflow-hidden bg-zinc-50">
                    <img src={bundle.image_url} alt={bundle.name} className="w-full h-full object-cover" />
                </div>
            )}
            <div className="flex-1">
                <div className="inline-block bg-rose-600 text-white text-[10px] uppercase tracking-widest px-2 py-0.5 font-bold">
                    -{Math.round(bundle.discount_pct)}%
                </div>
                <p className="font-display text-2xl mt-2 leading-tight">{bundle.name}</p>
                {bundle.description && <p className="text-sm text-zinc-600 mt-2">{bundle.description}</p>}
            </div>
            <div className="flex items-baseline gap-3 border-t border-zinc-100 pt-3">
                <p className="font-display text-3xl">€ {bundle.final_price.toFixed(2)}</p>
                <p className="text-sm text-zinc-400 line-through font-mono-dm">€ {bundle.base_price.toFixed(2)}</p>
                {savings > 0 && <p className="text-xs text-emerald-700 ml-auto">Bespaar € {savings.toFixed(2)}</p>}
            </div>
            {user ? (
                <button data-testid={`bundle-add-${bundle.slug}`} onClick={add} disabled={adding}
                    className="w-full inline-flex items-center justify-center gap-2 py-2.5 bg-zinc-950 text-white hover:bg-zinc-800 text-xs font-bold uppercase tracking-widest disabled:opacity-60">
                    {adding ? <Loader2 size={12} className="animate-spin" /> : <ShoppingBag size={12} />}
                    In mandje
                </button>
            ) : (
                <Link to={`/login?next=${encodeURIComponent("/stickers")}`}
                    className="text-center py-2.5 border border-zinc-950 text-xs font-bold uppercase tracking-widest hover:bg-zinc-950 hover:text-white transition-colors">
                    Log in om te kopen
                </Link>
            )}
        </article>
    );
}

function GiftProductCard({ product, user }) {
    const { fetchCart } = useCart();
    const [adding, setAdding] = useState(false);
    const hasDiscount = product.original_price && product.original_price > product.price;
    const add = async () => {
        if (!user) { toast.error("Log eerst in"); return; }
        setAdding(true);
        try {
            await api.post("/cart/items/gift-product", { gift_product_id: product.id, quantity: 1 });
            await fetchCart();
            toast.success(`${product.name} toegevoegd aan mandje`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Toevoegen mislukt");
        } finally { setAdding(false); }
    };
    return (
        <article data-testid={`gift-product-card-${product.slug}`} className="border border-zinc-200 bg-white flex flex-col">
            <div className="relative aspect-square border-b border-zinc-100 bg-zinc-50 overflow-hidden">
                {product.image_url ? (
                    <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <Sparkles size={32} className="text-zinc-300" />
                    </div>
                )}
                {product.badge && (
                    <span className="absolute top-2 left-2 bg-rose-600 text-white text-[10px] uppercase tracking-widest px-2 py-0.5 font-bold">
                        {product.badge}
                    </span>
                )}
                {!product.in_stock && (
                    <span className="absolute top-2 right-2 bg-amber-500 text-white text-[10px] uppercase tracking-widest px-2 py-0.5 font-bold">
                        Op bestelling
                    </span>
                )}
            </div>
            <div className="p-4 flex-1 flex flex-col gap-2">
                <p className="label-tiny text-zinc-400">{product.category}</p>
                <p className="font-display text-lg leading-tight">{product.name}</p>
                {product.short && <p className="text-xs text-zinc-600 line-clamp-2">{product.short}</p>}
                <div className="mt-auto pt-2 flex items-baseline gap-2">
                    <p className="font-display text-xl">€ {product.price.toFixed(2)}</p>
                    {hasDiscount && <p className="text-xs text-zinc-400 line-through font-mono-dm">€ {product.original_price.toFixed(2)}</p>}
                </div>
                {user ? (
                    <button data-testid={`gift-product-add-${product.slug}`} onClick={add} disabled={adding}
                        className="mt-2 inline-flex items-center justify-center gap-1.5 py-2 bg-zinc-950 text-white hover:bg-zinc-800 text-[10px] font-bold uppercase tracking-widest disabled:opacity-60">
                        {adding ? <Loader2 size={11} className="animate-spin" /> : <ShoppingBag size={11} />}
                        In mandje
                    </button>
                ) : (
                    <Link to={`/login?next=${encodeURIComponent("/stickers")}`}
                        className="mt-2 text-center py-2 border border-zinc-950 text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-950 hover:text-white transition-colors">
                        Log in om te kopen
                    </Link>
                )}
            </div>
        </article>
    );
}

function ShopProductCard({ product, user }) {
    const { fetchCart } = useCart();
    const [adding, setAdding] = useState(false);
    const hasDiscount = product.compare_at_price && product.compare_at_price > product.price;
    const stockTracked = product.stock_count !== null && product.stock_count !== undefined;
    const stockAvailable = stockTracked ? product.stock_count : (product.in_stock ? Infinity : 0);
    const canBuy = stockAvailable > 0 && product.in_stock;
    const add = async () => {
        if (!user) { toast.error("Log eerst in"); return; }
        if (!canBuy) { toast.info(stockAvailable === 0 ? "Dit product is uitverkocht" : "Dit product is momenteel niet op voorraad"); return; }
        setAdding(true);
        try {
            await api.post("/cart/items/shop-product", { shop_product_id: product.id, quantity: 1 });
            await fetchCart();
            toast.success(`${product.name} toegevoegd aan mandje`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Toevoegen mislukt");
        } finally { setAdding(false); }
    };
    return (
        <article data-testid={`shop-product-card-${product.slug}`} className="border border-zinc-200 bg-white flex flex-col">
            <div className="relative aspect-square border-b border-zinc-100 bg-zinc-50 overflow-hidden">
                {product.image_url ? (
                    <img src={product.image_url} alt={product.name} className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = "none"; }} />
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <Sparkles size={32} className="text-zinc-300" />
                    </div>
                )}
                {product.badge && (
                    <span className="absolute top-2 left-2 bg-rose-600 text-white text-[10px] uppercase tracking-widest px-2 py-0.5 font-bold">
                        {product.badge}
                    </span>
                )}
                <StockBadgeFloating stockTracked={stockTracked} stock={product.stock_count} inStock={product.in_stock} />
            </div>
            <div className="p-4 flex-1 flex flex-col gap-2">
                {product.category && <p className="label-tiny text-zinc-400">{product.category}</p>}
                <p className="font-display text-lg leading-tight">{product.name}</p>
                {product.description && <p className="text-xs text-zinc-600 line-clamp-2">{product.description}</p>}
                <div className="mt-auto pt-2 flex items-baseline gap-2">
                    <p className="font-display text-xl">€ {product.price.toFixed(2)}</p>
                    {hasDiscount && <p className="text-xs text-zinc-400 line-through font-mono-dm">€ {product.compare_at_price.toFixed(2)}</p>}
                </div>
                {user ? (
                    <button data-testid={`shop-product-add-${product.slug}`} onClick={add} disabled={adding || !canBuy}
                        className="mt-2 inline-flex items-center justify-center gap-1.5 py-2 bg-zinc-950 text-white hover:bg-zinc-800 text-[10px] font-bold uppercase tracking-widest disabled:opacity-60">
                        {adding ? <Loader2 size={11} className="animate-spin" /> : <ShoppingBag size={11} />}
                        {!product.in_stock ? "Niet beschikbaar" : (stockAvailable === 0 ? "Uitverkocht" : "In mandje")}
                    </button>
                ) : (
                    <Link to={`/login?next=${encodeURIComponent("/stickers")}`}
                        className="mt-2 text-center py-2 border border-zinc-950 text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-950 hover:text-white transition-colors">
                        Log in om te kopen
                    </Link>
                )}
            </div>
        </article>
    );
}

function StockBadgeFloating({ stockTracked, stock, inStock }) {
    // Bepaal welk label te tonen
    if (!inStock) {
        return (
            <span className="absolute top-2 right-2 bg-amber-500 text-white text-[10px] uppercase tracking-widest px-2 py-0.5 font-bold">
                Op bestelling
            </span>
        );
    }
    if (!stockTracked) {
        return (
            <span className="absolute top-2 right-2 bg-emerald-600 text-white text-[10px] uppercase tracking-widest px-2 py-0.5 font-bold">
                Op voorraad
            </span>
        );
    }
    if (stock === 0) {
        return (
            <span className="absolute top-2 right-2 bg-zinc-950 text-white text-[10px] uppercase tracking-widest px-2 py-0.5 font-bold">
                Uitverkocht
            </span>
        );
    }
    if (stock <= 3) {
        return (
            <span className="absolute top-2 right-2 bg-amber-500 text-white text-[10px] uppercase tracking-widest px-2 py-0.5 font-bold">
                Nog {stock} {stock === 1 ? "stuk" : "stuks"}
            </span>
        );
    }
    return (
        <span className="absolute top-2 right-2 bg-emerald-600 text-white text-[10px] uppercase tracking-widest px-2 py-0.5 font-bold">
            {stock} op voorraad
        </span>
    );
}
